<template>
  <div id="blog-post">
    <div class="container" v-if="post">
      <script v-html="jsonld" type="application/ld+json"></script>

      <b-row class="bg-light-blue py-5 mb-3 border-radius justify-content-center">
        <div class="post-header">
          <h1>{{ post.title }}</h1>
          <!-- New Post Metadata Section -->
          <div class="post-metadata">
            <div class="metadata-item author-item">
              <img :src="author.image" :alt="author.name" class="author-mini-avatar" />
              <span>{{ author.name }}</span>
            </div>
            <div class="metadata-separator"></div>
            <div class="metadata-item">
              <font-awesome-icon :icon="['fas', 'calendar']" />
              <span>{{ post.date | moment("DD MMM YYYY") }}</span>
            </div>
            <div class="metadata-separator"></div>
            <div class="metadata-item">
              <font-awesome-icon :icon="['fas', 'clock']" />
              <span>{{ readingTime }} min de lecture</span>
            </div>
          </div>
        </div>
      </b-row>

      <b-row class="justify-content-center">
        <!-- Left Column: TOC -->
        <b-col md="2" class="d-none d-md-block px-0">
          <div class="toc-wrapper sticky-top text-left">
            <h4 class="mb-3">Table des matières</h4>
            <ul class="toc-list">
              <li v-for="(item, index) in tableOfContents" :key="index"
                :class="{ 'active': activeHeading === item.id }">
                <a :href="`#${item.id}`" @click="scrollToSection(item.id)">
                  {{ item.text }}
                </a>
              </li>
            </ul>
          </div>
        </b-col>

        <!-- Center Column: Main Content -->
        <b-col md="7">
          <article class="mb-3">
            <div v-if="post.post_images && post.post_images.length > 0" class="mb-3">
              <img class="blog-img" :alt="post.title" :src="post.post_images[0].image_url" :width="1200" :height="630"
                style="max-width: 100%; height: auto; object-fit: contain;" loading="lazy" decoding="async" />
            </div>
            <div class="content-wrapper px-3">
              <div class="content" v-html="markdownToHtml(post.content)"></div>
              <!-- Author Section -->
              <div class="author-section">
                <div class="author-card">
                  <div class="author-avatar">
                    <img :src=author.image alt="Manon" />
                  </div>
                  <div class="author-info">
                    <div class="author-name">{{ author.name }}</div>
                    <p class="author-bio">
                      {{ author.bio }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="content-cta">
                <div class="cta-box">
                  <div class="cta-content">
                    <h3>Prêt à optimiser votre prospection immobilière ?</h3>
                    <p>Rejoignez les professionnels qui utilisent déjà Melo pour gagner du temps et trouver plus d'opportunités.</p>
                    <div class="cta-features-inline">
                      <span>✓ 900 sources</span>
                      <span>✓ Gain de temps</span>
                      <span>✓ 14 jours gratuits</span>
                    </div>
                    <b-button class="cta-button" @click="$router.push('/register')">
                      Commencer l'essai gratuit
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- Related Articles Section -->
              <div class="related-articles mt-3" v-if="relatedArticles.length > 0">
                <h3 class="mb-4">D'autres articles qui pourraient vous intéresser</h3>
                <b-row>
                  <b-col v-for="article in relatedArticles" :key="article.id" md="6" class="mb-4">
                    <router-link :to="{
                      name: 'blog-post',
                      params: {
                        category: article.category.slug,
                        slug: article.slug
                      }
                    }" class="related-article">
                      <article>
                        <div v-if="article.post_images && article.post_images.length > 0" class="related-img-wrapper">
                          <img :alt="article.slug" :src="article.post_images[0].image_url" class="related-img" />
                        </div>
                        <div class="related-title">{{ article.title }}</div>
                        <div class="text-grey">
                          {{ article.date | moment("from") }}
                        </div>
                      </article>
                    </router-link>
                  </b-col>
                </b-row>
              </div>
            </div>
          </article>
        </b-col>

        <!-- Right Column: CTA -->
        <b-col md="3" class="d-none d-md-block px-0 text-left">
          <div class="cta-wrapper sticky-top">
            <h4 class="mb-3">Découvrez Melo</h4>
            <p>Optimisez votre prospection immobilière</p>
            <ul class="cta-features">
              <li>✓ 900 sources en temps réel</li>
              <li>✓ Gain de temps considérable</li>
              <li>✓ API et webhooks</li>
              <li>✓ 14 jours d'essai gratuit</li>
            </ul>
            <b-button class="w-100 mt-3 bg-blue" @click="$router.push('/register')">
              Essayer gratuitement
            </b-button>
          </div>
        </b-col>
      </b-row>
    </div>
    <Footer />
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it';
const md = new MarkdownIt();
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Footer from "@/components/bottombar/Footer.vue";
import IconStars from "@/components/icons/IconStars.vue";
import IconBack from "@/components/icons/IconBack.vue";
import _ from "lodash";

export default {
  metaInfo() {
    if (!this.post) {
      return {
        title: 'Blog',
        titleTemplate: "%s — Melo"
      };
    }
    return {
      title: this.post.title,
      titleTemplate: "%s — Melo",
      meta: [
        { "http-equiv": "Content-Type", content: "text/html; charset=utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        {
          name: "description",
          content: this.post.summary || this.post.excerpt
        },
        {
          property: "og:title",
          content: this.post.title
        },
        { property: "og:site_name", content: "Melo" },
        { property: "og:type", content: "article" },
        {
          property: "og:url",
          content: `https://www.melo.io${this.$router.currentRoute.fullPath}`
        },
        {
          property: "og:image",
          content: this.post.image && this.post.image[0] ? this.post.image[0].url : ''
        },
        {
          property: "og:description",
          content: this.post.summary || this.post.excerpt
        },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: this.post.title },
        { name: "twitter:description", content: this.post.summary || this.post.excerpt },
        { name: "twitter:image", content: this.post.image && this.post.image[0] ? this.post.image[0].url : '' },

        // Article meta tags
        { property: "article:published_time", content: this.post.published_at },
        { property: "article:modified_time", content: this.post.updated_at || this.post.published_at },
        { property: "article:section", content: this.post.category },
        { property: "article:author", content: this.post.author },

        // Additional meta tags
        { name: "robots", content: "index, follow" },
        { name: "author", content: this.post.author },
        { name: "copyright", content: `© ${new Date().getFullYear()} Melo` }
      ],
      link: [
        {
          rel: "canonical",
          href: `https://www.melo.io${this.$router.currentRoute.fullPath}`
        }
      ],
    };
  },
  name: "blogPost",
  components: {
    Footer,
    IconStars,
    IconBack,
    FontAwesomeIcon
  },
  data() {
    return {
      post: null,
      tableOfContents: [],
      activeHeading: '',
      author: {
        name: 'Manon',
        role: 'Marketing chez Melo',
        bio: "Experte en marketing avec plus de 8 ans d'expérience en rédaction et stratégie de contenu.",
        image: require('@/assets/img/manon.webp'),
        social: {
          linkedin: 'https://www.linkedin.com/in/your-profile',
          twitter: 'https://twitter.com/your-profile'
        }
      }
    };
  },
  computed: {
    relatedArticles() {
      if (!this.post) return [];

      return require('../../articles.json')
        .filter(article =>
          // Exclude current article
          article.id !== this.post.id &&
          // Optionally prioritize articles from same category
          article.category_id == this.post.category_id
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 4);
    },
    isPostLoading() {
      return this.post == null;
    },
    readingTime() {
      if (!this.post.content) return 0;
      // Average reading speed: 200 words per minute
      const words = this.post.content.trim().split(/\s+/).length;
      return Math.ceil(words / 200);
    },
    jsonld() {
      if (!this.post) return {};

      const imageUrl = this.post.post_images &&
        this.post.post_images.length > 0 ?
        this.post.post_images[0].image_url :
        '';

      const wordCount = this.post.content ?
        this.post.content.trim().split(/\s+/).length :
        0;

      return {
        "@context": "http://schema.org",
        "@graph": [
          {
            "@type": "BlogPosting", // Changed from Article to BlogPosting
            headline: this.post.title,
            description: this.post.summary,
            genre: "immobilier",
            wordCount: wordCount,
            datePublished: this.post.date,
            dateModified: this.post.updated_at || this.post.date, // Add modified date
            articleBody: this.post.content,
            image: {
              "@type": "ImageObject",
              url: imageUrl,
              width: "1200", // Add image dimensions
              height: "630"
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://www.melo.io/${this.post.category}/${this.post.slug}/`
            },
            publisher: {
              "@type": "Organization",
              "@id": "https://www.melo.io",
              name: "Melo",
              logo: {
                "@type": "ImageObject",
                url: "https://www.melo.io/assets/socials/fb-profile.png",
                width: 1000,
                height: 1000,
              },
            },
            author: {
              "@type": "Person",
              name: this.author.name,
              jobTitle: this.author.role,
              description: this.author.bio,
              image: this.author.image
            },
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@type": "WebPage",
                  "@id": "https://www.melo.io",
                  name: "Le moteur de recherche des professionnels de l'immobilier - Blog",
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@type": "WebPage",
                  "@id": `https://www.melo.io/${this.post.category}/${this.post.slug}/`,
                  name: this.post.title,
                  image: imageUrl,
                },
              },
            ],
          },
        ],
      };
    },
  },
  methods: {
    markdownToHtml(markdown) {
      // Render markdown to HTML
      let html = md.render(markdown);
      
      // Basic sanitization to remove potentially problematic elements
      // Remove any <link> tags that might be trying to load external stylesheets
      html = html.replace(/<link\s+[^>]*>/gi, '');
      
      // Remove any <style> tags that might be included in the content
      html = html.replace(/<style\s*[^>]*>[\s\S]*?<\/style>/gi, '');
      
      return html;
    },
    generateTableOfContents() {
      const content = document.querySelector('.content');
      if (!content) return;

      const headings = content.querySelectorAll('h2');
      this.tableOfContents = Array.from(headings).map((heading, index) => {
        // Create a URL-friendly id from the heading text
        const id = heading.textContent
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

        heading.id = id;
        return {
          id,
          text: heading.textContent,
        };
      });
    },

    scrollToSection(id) {
      // Prevent default anchor behavior
      event.preventDefault();

      const element = document.getElementById(id);
      if (element) {
        // Add offset for any fixed headers if needed
        const offset = 100; // Adjust this value based on your header height
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        // Update the URL without triggering a route change
        const currentPath = this.$route.path;
        const newUrl = `${currentPath}#${id}`;
        history.pushState(null, '', newUrl);
      }
    },

    handleScroll() {
      if (!this.tableOfContents.length) return;

      const headings = this.tableOfContents.map(toc =>
        document.getElementById(toc.id)
      );

      // Find the current section
      let currentSection = null;
      for (const heading of headings) {
        if (!heading) continue;

        const rect = heading.getBoundingClientRect();
        const offset = 150; // Adjust this to match your header height + some padding

        if (rect.top <= offset) {
          currentSection = heading.id;
        } else {
          break;
        }
      }

      this.activeHeading = currentSection;
    },
    async fetchPost(slug, categorySlug) {
      try {
        this.$store.commit("UPDATE_LOADING", true);
        const articles = require('../../articles.json');

        const article = articles.find(article =>
          article.category &&
          article.category.slug === categorySlug &&  // Changed to match by slug instead of name
          article.slug === slug
        );

        if (article) {
          const imageUrl = article.post_images &&
            article.post_images.length > 0 ?
            article.post_images[0].image_url :
            null;

          this.post = {
            ...article,
            category: article.category.name,
            image: imageUrl ? [{
              signedUrl: imageUrl,
              url: imageUrl
            }] : []
          };

        } else {
          throw new Error('Article not found');
        }

        return article;
      } catch (error) {
        console.error('Error fetching post:', error);
        this.$router.push('/blog');
      } finally {
        this.$store.commit("UPDATE_LOADING", false);
      }
    }
  },
  mounted() {
    const { slug, category } = this.$route.params;
    this.fetchPost(slug, category).then(() => {
      this.$nextTick(() => {
        this.generateTableOfContents();
        window.addEventListener('scroll', this.handleScroll);
      });
    });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  }
};
</script>

<style lang="scss" scoped>
@import "~bootstrap/scss/functions";
@import "~bootstrap/scss/variables";
@import "~bootstrap/scss/mixins";

h1 {
  text-overflow: ellipsis;
  letter-spacing: 0.2px;
  color: $body-text;
  font-weight: bold;

  @include media-breakpoint-up(md) {
    padding-left: 12rem;
    padding-right: 12rem;
  }
}

.blog-img {
  width: 100%;
  border-radius: $radius;
}

.content-wrapper {
  @include media-breakpoint-up(md) {
    padding-left: 3.5rem;
    padding-right: 3.5rem;
  }

  text-align: left;
  font-size: 1.1rem;
}

.author {
  img {
    border-radius: 50px;
  }
}
.content-cta {
  margin: 2rem 0;
  
  .cta-box {
    background: $blue;
    border-radius: $radius;
    padding: 2.5rem;
    color: white;
    text-align: center;
  }

  .cta-content {
    max-width: 600px;
    margin: 0 auto;

    h3 {
      font-size: 1.8rem;
      font-weight: bold;
      margin-bottom: 1rem;
      color: white;
    }

    p {
      font-size: 1.1rem;
      margin-bottom: 1.5rem;
      opacity: 0.9;
    }
  }

  .cta-features-inline {
    display: flex;
    justify-content: center;
    gap: 2rem;
    margin-bottom: 2rem;
    flex-wrap: wrap;

    span {
      font-size: 1rem;
      opacity: 0.9;
    }
  }

  .cta-button {
    background-color: white;
    color: $blue;
    border: none;
    padding: 0.8rem 2rem;
    font-weight: 600;
    font-size: 1.1rem;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      background-color: white !important;
      color: $blue !important;
    }
  }

  @include media-breakpoint-down(sm) {
    .cta-features-inline {
      gap: 1rem;
      flex-direction: column;
    }

    .cta-box {
      padding: 2rem 1.5rem;
    }

    .cta-content h3 {
      font-size: 1.5rem;
    }
  }
}
</style>

<style lang="scss">
#blog-post {
  h2 {
    font-weight: bold;
    padding-bottom: 1rem;
    padding-top: 1rem;
  }

  .content {
    img {
      width: 100%;
      padding-top: 1rem;
      padding-bottom: 1rem;
    }

    ul {
      padding: 0;
    }

    li {
      padding-left: 24px;
      position: relative;
      padding-bottom: 0.8rem;
    }

    ul li::before {
      background-color: $blue;
      content: "";
      display: block;
      height: 8px;
      left: 0px;
      position: absolute;
      top: 8px;
      width: 8px;
      border-radius: 4px;
    }
  }

  .content-wrapper {
    svg {
      align-self: center;
      margin-right: 1rem;
      width: 1.5rem;

      path {
        fill: $blue;
      }
    }
  }

  .row-card {
    svg {
      align-self: start;
      margin-right: 1rem;
      width: 4rem;

      path {
        fill: white !important;
      }
    }
  }

  .toc-sidebar,
  .cta-sidebar {
    position: sticky;
    top: 2rem;
    height: fit-content;
    width: 250px;
    padding: 1rem;

    @include media-breakpoint-down(md) {
      display: none;
    }
  }

  .toc-sidebar {
    left: 0;
    margin-left: -280px;
  }

  .cta-sidebar {
    right: 0;
    margin-right: -280px;
  }

  .toc-wrapper,
  .cta-wrapper {
    background: white;
    padding: 1.5rem;
    border-radius: $radius;
    box-shadow: $box-shadow;
  }

  .toc-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 0.8rem;
      font-size: 0.9rem;
      padding-left: 0;
      line-height: 1.4;

      &::before {
        display: none;
      }

      &.active a {
        color: $blue;
        font-weight: bold;
      }

      a {
        color: $body-text;
        text-decoration: none;
        transition: color 0.2s ease;
        display: block;

        &:hover {
          color: $blue;
        }
      }
    }
  }

  html {
    scroll-behavior: smooth;
    scroll-padding-top: 100px; // Adjust based on your header height
  }

  .cta-features {
    list-style: none;
    padding: 0;
    margin: 1rem 0;

    li {
      margin-bottom: 0.8rem;
      padding-left: 0;
      color: $body-text;
      font-size: 0.9rem;

      &::before {
        display: none;
      }
    }
  }

  .author-section {
    padding-top: 1rem;
  }

  .author-card {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    padding: 1.5rem;
    background: white;
    border-radius: $radius;
    box-shadow: $box-shadow;

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 1rem;
    }
  }

  .author-avatar {
    flex-shrink: 0;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid $blue;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .author-info {
    flex-grow: 1;
  }

  .author-name {
    margin: 0;
    font-size: 1.2rem;
    font-weight: bold;
    color: $body-text;
  }

  .author-role {
    color: $blue;
    font-size: 0.9rem;
    margin: 0.3rem 0 0.8rem;
  }

  .author-bio {
    color: $body-text;
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0;
  }

  .post-header {
    text-align: center;
    margin: 0 auto;

    h1 {
      margin-bottom: 1.5rem;
    }
  }

  .post-metadata {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    color: $body-text;
    font-size: 0.95rem;

    @include media-breakpoint-down(sm) {
      flex-wrap: wrap;
      gap: 0.5rem;
    }
  }

  .metadata-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      font-size: 0.9em;
      color: $greyer;
    }
  }

  .author-item {
    font-weight: 500;
  }

  .author-mini-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid white;
  }

  .metadata-separator {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: $greyer;
    opacity: 0.5;

    @include media-breakpoint-down(sm) {
      display: none;
    }
  }

  // Responsive adjustments
  @include media-breakpoint-down(sm) {
    .post-metadata {
      flex-direction: column;
      align-items: center;
    }

    .metadata-item {
      justify-content: center;
    }
  }

  // Optional: Add hover effect on author
  .author-item {
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: $blue;
    }
  }

  .related-articles {
    border-top: 1px solid #eee;
    padding-top: 2rem;

    h3 {
      font-size: 1.5rem;
      font-weight: bold;
      color: $body-text;
    }
  }

  .related-article {
    display: block;
    text-decoration: none;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-3px);
      text-decoration: none;

      .related-title {
        color: $blue;
      }
    }
  }

  .related-img-wrapper {
    margin-bottom: 1rem;
    border-radius: $radius;
    overflow: hidden;
  }

  .related-img {
    width: 100%;
    height: 150px;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .related-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: $body-text;
    margin-bottom: 0.5rem;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .text-grey {
    font-size: 0.9rem;
    color: $greyer;
  }
}

</style>